import { BaseHandler } from "./base.handler";
import type { AuthMethod, WebSocketResponse } from "@/types";
import {
  type SetPlayerResponse,
  type AuthenticateResponse,
  SetPlayer,
  Authenticate,
  RESULT,
  isSetPlayerResponse,
  isAuthenticateResponse,
} from "@/types";
import { WebSocketService } from "../websocket.service";
import { configHandler } from "./config.handler";
import { useAppStore } from "@/stores";
import {
  sha512,
  getUserIdCookie,
  getRegistrationDateCookie,
  parseRegistrationDateToISO,
} from "@/utils";

export class AuthHandler extends BaseHandler {
  public authenticating = false;
  public authMethod: AuthMethod = null;

  authenticate(): void {
    const playerHash = getUserIdCookie();

    if (!playerHash) {
      this.logger.error("Missing cookies for authentication", { playerHash });
      return;
    }

    this.authMethod = "legacy";
    this.authenticating = true;

    useAppStore.getState().setPlayerHash(playerHash);

    const wsService = WebSocketService.getInstance();
    wsService.send(SetPlayer, { player: playerHash });
  }

  logout(): void {
    this.authenticating = false;
    this.authMethod = null;
    useAppStore.getState().clearAuth();
  }

  canHandle(messageType: string): boolean {
    return messageType === SetPlayer || messageType === Authenticate;
  }

  async handle(
    message: WebSocketResponse,
  ): Promise<void> {
    if (!this.validateMessage(message)) {
      this.logger.error("Invalid message format", message);
      return;
    }

    if (isSetPlayerResponse(message)) {
      if (this.authMethod === null || this.authMethod === "legacy") {
        this.authMethod = "legacy";
        await this.handleSetPlayerResponse(message);
        return;
      }
    }

    if (isAuthenticateResponse(message)) {
      this.handleAuthenticateResponse(message);
      return;
    }

  }

  private async handleSetPlayerResponse(
    message: SetPlayerResponse
  ): Promise<void> {
    if (message.result === RESULT.OK) {
      const authToken = message.auth;

      if (authToken === null) {
        this.logger.error("set_player ok but missing auth token", message);
        useAppStore.getState().setAuthError("Failed to get auth token");
        return;
      }

      const store = useAppStore.getState();
      store.setToken(authToken);
      store.setAuthMethod(this.authMethod);

      const signedToken = await this.generateSignedToken(authToken);

      if (!signedToken) {
        this.logger.error("Failed to generate signed token", null);
        useAppStore.getState().setAuthError("Failed to generate signed token");
        return;
      }

      const wsService = WebSocketService.getInstance();
      wsService.send(Authenticate, { token: signedToken });
    } else {
      this.logger.error("set_player failed", message);
      useAppStore.getState().setAuthError("Failed to get auth token");
    }
  }

  private handleAuthenticateResponse(
    message: AuthenticateResponse
  ): void {
    const store = useAppStore.getState();
    if (message.result === RESULT.OK) {
      this.authenticating = false;
      store.setAuthMethod(this.authMethod);
      store.setAuthenticated(true);
      this.logger.log("Authentication successful");
      // Configure is a per-connection step: send it on every successful auth so
      // it re-runs on reconnects too (not just on the authenticated transition).
      // The configure response sets language/currency, which drives the subscribe.
      configHandler.sendConfiguration();
    } else {
      this.authenticating = false;
      store.setAuthError("Authentication failed");
      store.setAuthenticated(false);
      this.logger.error("Authentication failed", message);
    }
  }
  private async generateSignedToken(
    authToken: string
  ): Promise<string | null> {
    try {
      const playerHash = getUserIdCookie();
      const registrationDate = getRegistrationDateCookie();

      if (!playerHash || !registrationDate) {
        this.logger.error("Missing cookies for token signing", {
          playerHash,
          registrationDate,
        });
        return null;
      }

      const registrationDateISO = parseRegistrationDateToISO(registrationDate);
      if (!registrationDateISO) {
        this.logError(
          "Failed to parse registration date to ISO format",
          registrationDate
        );
        return null;
      }

      const hash = await sha512(`${playerHash}.${registrationDateISO}`);
      const signedToken = `${hash}:${authToken}`;
      return signedToken;
    } catch (error) {
      this.logError("Error generating signed token", error);
      return null;
    }
  }
}


// Singleton Export
export const authHandler = new AuthHandler();
